import React, { useState, useEffect } from 'react';
import config from './config';
import { formatMetricCost } from './metricFormatting';
import './App.css';

// API Gateway caps a request payload at 10 MB, and base64 encoding inflates a
// PDF by about a third. Reject anything above this raw size in the browser so
// the user gets a clear message instead of an opaque network failure.
const MAX_PDF_BYTES = 6 * 1024 * 1024;

const OVERSIZED_PDF_MESSAGE =
  'This PDF is too large to upload. Please upload a text-based PDF of 20 pages or fewer (under 6 MB).';

const MODELS = [
  { id: 'amazon.nova-lite-v1:0', name: 'Amazon Nova Lite' },
  { id: 'us.anthropic.claude-sonnet-4-6', name: 'Claude Sonnet 4.6' },
  { id: 'us.anthropic.claude-opus-4-6-v1', name: 'Claude Opus 4.6' },
  { id: 'openai.gpt-oss-20b-1:0', name: 'GPT-OSS-20B' },
  { id: 'arn:aws:bedrock:us-east-1:727646482542:imported-model/m2ubtiesnm89', name: 'Llama 3.2 3B (Custom)' },
];

function App() {
  const [prompt, setPrompt] = useState('');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [user, setUser] = useState(null);
  const [selectedModel, setSelectedModel] = useState(MODELS[0].id);
  const [compareMode, setCompareMode] = useState(false);
  const [compareResults, setCompareResults] = useState(null);
  const [compareLoading, setCompareLoading] = useState(false);
  const [showAnalysis, setShowAnalysis] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [fileInputKey, setFileInputKey] = useState(0);
  const [documentSession, setDocumentSession] = useState(() => {
    try {
      const savedSession = sessionStorage.getItem('documentSession');
      return savedSession ? JSON.parse(savedSession) : null;
    } catch {
      sessionStorage.removeItem('documentSession');
      return null;
    }
  });

  useEffect(() => {
    const hash = window.location.hash;
    if (hash) {
      const params = new URLSearchParams(hash.substring(1));
      const idToken = params.get('id_token');
      const accessToken = params.get('access_token');
      if (idToken && accessToken) {
        localStorage.setItem('id_token', idToken);
        localStorage.setItem('access_token', accessToken);
        const payload = JSON.parse(atob(idToken.split('.')[1]));
        setUser({ email: payload.email });
        window.history.replaceState(null, '', window.location.pathname);
      }
    } else {
      const idToken = localStorage.getItem('id_token');
      if (idToken) {
        try {
          const payload = JSON.parse(atob(idToken.split('.')[1]));
          if (payload.exp * 1000 > Date.now()) {
            setUser({ email: payload.email });
          } else {
            handleLogout();
          }
        } catch {
          handleLogout();
        }
      } else {
        redirectToLogin();
      }
    }
  }, []);

  const redirectToLogin = () => {
    const { domain, clientId, redirectUri } = config.cognito;
    window.location.href = `https://${domain}/login?client_id=${clientId}&response_type=token&scope=email+openid&redirect_uri=${encodeURIComponent(redirectUri)}`;
  };

  const clearDocumentSession = () => {
    sessionStorage.removeItem('documentSession');
    setDocumentSession(null);
    setSelectedFile(null);
    setUploadError('');
    setFileInputKey((key) => key + 1);
  };

  const handleLogout = () => {
    localStorage.removeItem('id_token');
    localStorage.removeItem('access_token');
    clearDocumentSession();
    setUser(null);
    const { domain, clientId, redirectUri } = config.cognito;
    window.location.href = `https://${domain}/logout?client_id=${clientId}&logout_uri=${encodeURIComponent(redirectUri)}`;
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0] || null;
    setUploadError('');

    if (!file) {
      setSelectedFile(null);
      return;
    }

    const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
    if (!isPdf) {
      setSelectedFile(null);
      setUploadError('Please select a PDF document.');
      setFileInputKey((key) => key + 1);
      return;
    }

    if (file.size > MAX_PDF_BYTES) {
      setSelectedFile(null);
      setUploadError(OVERSIZED_PDF_MESSAGE);
      setFileInputKey((key) => key + 1);
      return;
    }

    setSelectedFile(file);
  };

  const readFileAsBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || '');
      const commaIndex = result.indexOf(',');
      if (commaIndex === -1) {
        reject(new Error('Could not read the selected PDF.'));
        return;
      }
      resolve(result.slice(commaIndex + 1));
    };
    reader.onerror = () => reject(new Error('Could not read the selected PDF.'));
    reader.readAsDataURL(file);
  });

  const handleUpload = async (event) => {
    event.preventDefault();
    if (!selectedFile || uploading) return;

    setUploading(true);
    setUploadError('');

    if (selectedFile.size > MAX_PDF_BYTES) {
      setUploadError(OVERSIZED_PDF_MESSAGE);
      setUploading(false);
      return;
    }

    try {
      const file = await readFileAsBase64(selectedFile);
      const idToken = localStorage.getItem('id_token');

      let response;
      try {
        response = await fetch(`${config.uploadApiUrl}/upload`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': idToken
          },
          body: JSON.stringify({ file, filename: selectedFile.name })
        });
      } catch {
        // A rejected request never reaches the backend, so no page count or
        // server error is available to report. Oversized payloads are the
        // common cause, so point the user at the document limits.
        throw new Error(
          'The upload could not be sent. This usually means the PDF is too large. ' +
          'Please upload a text-based PDF of 20 pages or fewer (under 6 MB).'
        );
      }

      if (response.status === 413) {
        throw new Error(OVERSIZED_PDF_MESSAGE);
      }

      let data;
      try {
        data = await response.json();
      } catch {
        throw new Error(
          response.ok
            ? 'The server returned an unreadable response. Please try uploading the PDF again.'
            : `Upload failed (status ${response.status}). Please upload a text-based PDF of 20 pages or fewer.`
        );
      }

      if (!response.ok) {
        throw new Error(data.error || 'Failed to upload PDF');
      }

      const session = {
        sessionId: data.sessionId,
        filename: selectedFile.name,
        pageCount: data.pageCount
      };
      sessionStorage.setItem('documentSession', JSON.stringify(session));
      setDocumentSession(session);
      setSelectedFile(null);
      setFileInputKey((key) => key + 1);
      setMessages([]);
      setCompareResults(null);
      setError('');
    } catch (err) {
      setUploadError(err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveDocument = () => {
    clearDocumentSession();
    setMessages([]);
    setCompareResults(null);
    setError('');
  };

  const callModel = async (promptText, modelId) => {
    const idToken = localStorage.getItem('id_token');
    const requestBody = { prompt: promptText, modelId };
    if (documentSession?.sessionId) {
      requestBody.sessionId = documentSession.sessionId;
    }

    const res = await fetch(`${config.apiUrl}/chat`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': idToken },
      body: JSON.stringify(requestBody)
    });
    const data = await res.json();
    if (!res.ok) {
      const message = data.error || 'Failed to get response';
      if (/document session expired|document session.*not found/i.test(message)) {
        clearDocumentSession();
      }
      throw new Error(message);
    }
    return data;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!prompt.trim() || loading) return;
    const userMessage = prompt.trim();
    setPrompt('');
    setError('');
    setLoading(true);
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);

    try {
      const data = await callModel(userMessage, selectedModel);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: data.response,
        modelName: data.modelName,
        metrics: data.metrics
      }]);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCompare = async (e) => {
    e.preventDefault();
    if (!prompt.trim() || compareLoading) return;
    const userMessage = prompt.trim();
    setPrompt('');
    setError('');
    setCompareLoading(true);
    setCompareResults({ prompt: userMessage, results: [] });

    try {
      const results = await Promise.allSettled(
        MODELS.map(m => callModel(userMessage, m.id))
      );
      setCompareResults({
        prompt: userMessage,
        results: results.map((r, i) => ({
          model: MODELS[i],
          ...(r.status === 'fulfilled'
            ? { data: r.value }
            : { error: r.reason.message })
        }))
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setCompareLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="app">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Redirecting to login...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="app">
      <header className="header">
        <h1>Nova Chat</h1>
        <div className="header-controls">
          <label className="compare-toggle">
            <input
              type="checkbox"
              checked={compareMode}
              onChange={(e) => { setCompareMode(e.target.checked); setShowAnalysis(false); }}
            />
            Compare Mode
          </label>
          <button
            className={`analysis-btn ${showAnalysis ? 'active' : ''}`}
            onClick={() => { setShowAnalysis(!showAnalysis); setCompareMode(false); }}
          >
            📊 Analysis
          </button>
          <div className="user-info">
            <span>{user.email}</span>
            <button onClick={handleLogout} className="logout-btn">Sign Out</button>
          </div>
        </div>
      </header>

      <main className="chat-container">
        <section className="document-panel" aria-labelledby="document-panel-title">
          <div className="document-panel-copy">
            <h2 id="document-panel-title">Document Q&amp;A</h2>
            <p>Upload one text-based PDF of up to 20 pages (under 6 MB). Its full text will stay in context for this session.</p>
          </div>

          <form onSubmit={handleUpload} className="document-upload-controls">
            <input
              key={fileInputKey}
              type="file"
              accept=".pdf,application/pdf"
              onChange={handleFileChange}
              disabled={uploading}
              aria-label="Select a PDF document"
            />
            <button type="submit" disabled={!selectedFile || uploading}>
              {uploading ? 'Uploading…' : documentSession ? 'Replace document' : 'Upload PDF'}
            </button>
          </form>

          {selectedFile && !uploading && (
            <div className="document-selected">Selected: {selectedFile.name}</div>
          )}

          {uploadError && <div className="document-error" role="alert">{uploadError}</div>}

          {documentSession && (
            <div className="document-active" role="status">
              <div>
                <strong>Document ready</strong>
                <span>{documentSession.filename} · {documentSession.pageCount} {documentSession.pageCount === 1 ? 'page' : 'pages'}</span>
              </div>
              <button type="button" onClick={handleRemoveDocument} disabled={uploading}>Remove</button>
            </div>
          )}
        </section>

        {showAnalysis ? (
          <div className="analysis-view">
            <h2>Model Comparison Analysis</h2>
            <h3>Model Specifications</h3>
            <table><thead><tr><th>Model</th><th>Provider</th><th>Parameters</th><th>Context Window</th><th>Max Output</th></tr></thead><tbody>
              <tr><td>Amazon Nova Lite</td><td>Amazon</td><td>Undisclosed</td><td>300K</td><td>5K</td></tr>
              <tr><td>Claude Sonnet 4.6</td><td>Anthropic</td><td>Undisclosed</td><td>1M</td><td>64K</td></tr>
              <tr><td>Claude Opus 4.6</td><td>Anthropic</td><td>Undisclosed</td><td>1M</td><td>128K</td></tr>
              <tr><td>GPT-OSS-20B</td><td>OpenAI</td><td>20B (MoE)</td><td>128K</td><td>16K</td></tr>
            </tbody></table>

            <h3>Pricing (US East, On-Demand, per 1M tokens)</h3>
            <table><thead><tr><th>Model</th><th>Input Cost</th><th>Output Cost</th><th>Cost per typical request</th></tr></thead><tbody>
              <tr><td>Amazon Nova Lite</td><td>$0.06</td><td>$0.24</td><td>$0.000180</td></tr>
              <tr><td>GPT-OSS-20B</td><td>$0.07</td><td>$0.30</td><td>$0.000220</td></tr>
              <tr><td>Claude Sonnet 4.6</td><td>$3.00</td><td>$15.00</td><td>$0.010500</td></tr>
              <tr><td>Claude Opus 4.6</td><td>$5.00</td><td>$25.00</td><td>$0.017500</td></tr>
            </tbody></table>
            <p>Nova Lite and GPT-OSS-20B are approximately <strong>50–100x cheaper</strong> than the Claude models per request.</p>

            <h3>Performance Characteristics</h3>
            <h4>Latency</h4>
            <ul>
              <li><strong>Nova Lite</strong>: Fastest TTFT (~200–400ms). In-region deployment with minimal routing overhead.</li>
              <li><strong>GPT-OSS-20B</strong>: Fast TTFT (~300–600ms). MoE architecture activates subset of parameters for efficient inference.</li>
              <li><strong>Claude Sonnet 4.6</strong>: Moderate (~500–1500ms TTFT). Cross-region inference adds routing overhead.</li>
              <li><strong>Claude Opus 4.6</strong>: Highest (~1–4s TTFT). Deeper reasoning before generating output.</li>
            </ul>
            <h4>Response Quality</h4>
            <ul>
              <li><strong>Claude Opus 4.6</strong>: Best overall. Excels at complex reasoning, nuanced analysis, long-form content.</li>
              <li><strong>Claude Sonnet 4.6</strong>: Near-Opus quality at lower latency. Strong at coding and structured output.</li>
              <li><strong>GPT-OSS-20B</strong>: Good reasoning with chain-of-thought. Occasionally exposes reasoning traces.</li>
              <li><strong>Nova Lite</strong>: Adequate for simple Q&amp;A. Extremely fast and cost-effective.</li>
            </ul>

            <h3>Use Case Recommendations</h3>
            <table><thead><tr><th>Use Case</th><th>Recommended Model</th><th>Rationale</th></tr></thead><tbody>
              <tr><td>High-volume chatbot / FAQ</td><td>Nova Lite</td><td>Lowest cost, fastest response</td></tr>
              <tr><td>Code generation</td><td>Claude Sonnet 4.6</td><td>Strong coding ability, reasonable latency</td></tr>
              <tr><td>Complex analysis & research</td><td>Claude Opus 4.6</td><td>Highest reasoning quality</td></tr>
              <tr><td>Cost-sensitive batch processing</td><td>GPT-OSS-20B</td><td>Good reasoning at near-Nova pricing</td></tr>
              <tr><td>Real-time interactive chat</td><td>Nova Lite or Sonnet</td><td>Low latency for UX</td></tr>
              <tr><td>Agentic workflows</td><td>Claude Opus 4.6</td><td>Sustains multi-step tasks</td></tr>
            </tbody></table>

            <h3>Trade-offs</h3>
            <ol>
              <li><strong>Cost vs. Quality</strong>: Claude models deliver better responses at 50–100x the cost. Intelligent routing can optimize spend.</li>
              <li><strong>Latency vs. Depth</strong>: Opus is most thoughtful but slowest. Sonnet offers the best balance for user-facing apps.</li>
              <li><strong>Open vs. Proprietary</strong>: GPT-OSS-20B is open-weight (fine-tunable) but reasoning traces may leak into responses.</li>
              <li><strong>Regional Availability</strong>: Nova Lite and GPT-OSS are in-region; Claude models use cross-region inference profiles.</li>
              <li><strong>Context Window</strong>: Claude's 1M tokens suits document analysis; GPT-OSS's 128K is sufficient for chat.</li>
            </ol>

            <h3>Conclusion</h3>
            <p><strong>Claude Sonnet 4.6</strong> offers the best balance of quality, speed, and cost for general-purpose chat. <strong>Nova Lite</strong> provides excellent value for cost-sensitive deployments. <strong>Opus 4.6</strong> should be reserved for deep reasoning tasks, and <strong>GPT-OSS-20B</strong> is ideal for open-weight flexibility at minimal cost.</p>
          </div>
        ) : !compareMode ? (
          <>
            <div className="messages">
              {messages.length === 0 && (
                <div className="welcome"><p>Ask me anything</p></div>
              )}
              {messages.map((msg, index) => (
                <div key={index} className={`message ${msg.role}`}>
                  <div className="message-content">
                    {msg.modelName && <div className="model-label">{msg.modelName}</div>}
                    {msg.content}
                  </div>
                  {msg.metrics && (
                    <div className="metrics">
                      <span>⏱ TTFT {msg.metrics.ttftMs}ms | Total {msg.metrics.totalMs}ms</span>
                      <span>📝 {msg.metrics.inputTokens}→{msg.metrics.outputTokens} ({msg.metrics.totalTokens} total) tokens</span>
                      <span>💰 {formatMetricCost(msg.metrics)}</span>
                    </div>
                  )}
                </div>
              ))}
              {loading && (
                <div className="message assistant">
                  <div className="message-content">
                    <div className="typing-indicator"><span></span><span></span><span></span></div>
                  </div>
                </div>
              )}
            </div>

            {error && <div className="error">{error}</div>}

            <form onSubmit={handleSubmit} className="input-form">
              <select
                value={selectedModel}
                onChange={(e) => setSelectedModel(e.target.value)}
                className="model-select"
              >
                {MODELS.map(m => (
                  <option key={m.id} value={m.id}>{m.name}</option>
                ))}
              </select>
              <input
                type="text"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="Type your message..."
                disabled={loading}
              />
              <button type="submit" disabled={loading || !prompt.trim()} aria-label="Send">
                ➤
              </button>
            </form>
          </>
        ) : (
          <>
            {compareResults && (
              <div className="compare-view">
                <div className="compare-prompt">
                  <strong>Prompt:</strong> {compareResults.prompt}
                </div>
                <div className="compare-grid">
                  {compareResults.results.map((r, i) => (
                    <div key={i} className="compare-card">
                      <div className="compare-card-header">{r.model.name}</div>
                      {r.error ? (
                        <div className="compare-card-error">{r.error}</div>
                      ) : r.data ? (
                        <>
                          <div className="compare-card-body">{r.data.response}</div>
                          {r.data.metrics && (
                            <div className="metrics">
                              <span>⏱ TTFT {r.data.metrics.ttftMs}ms | Total {r.data.metrics.totalMs}ms</span>
                              <span>📝 {r.data.metrics.inputTokens}→{r.data.metrics.outputTokens} ({r.data.metrics.totalTokens} total) tokens</span>
                              <span>💰 {formatMetricCost(r.data.metrics)}</span>
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="compare-card-body"><div className="typing-indicator"><span></span><span></span><span></span></div></div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {compareLoading && !compareResults?.results.length && (
              <div className="loading-container"><div className="spinner"></div><p>Querying all models...</p></div>
            )}

            {error && <div className="error">{error}</div>}

            <form onSubmit={handleCompare} className="input-form">
              <input
                type="text"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="Enter a prompt to compare across all models..."
                disabled={compareLoading}
              />
              <button type="submit" disabled={compareLoading || !prompt.trim()} aria-label="Compare">
                ⚡
              </button>
            </form>
          </>
        )}
      </main>
    </div>
  );
}

export default App;
